import {
  Button,
  FormInput,
  FormPassword,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { z } from "zod";
import type { RoleDTO } from "@/modules/roles/presentation/api/client";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { AvatarPresignedUpload } from "@/shared/ui/AvatarPresignedUpload";
import { FormInfiniteCombobox } from "@/shared/ui/FormInfiniteCombobox";

const UserFormSchema = z.object({
  email: z.string().email({ message: "ກະລຸນາໃສ່ອີເມວໃຫ້ຖືກຕ້ອງ" }),
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່"),
  password: z.string().min(6, "ລະຫັດຜ່ານຕ້ອງຢ່າງນ້ອຍ 6 ຕົວອັກສອນ").optional(),
  roleId: z.string().min(1, "ຕ້ອງເລືອກບົດບາດ").optional(),
  imageKey: z.string().optional().nullable(),
});

export type UserFormValues = z.infer<typeof UserFormSchema>;

export function UserForm({
  initialValues,
  onSubmit,
  submitting,
}: {
  initialValues?: Partial<UserFormValues>;
  onSubmit: (values: {
    email: string;
    name: string;
    password?: string;
    roleId?: string;
    imageKey?: string | null;
  }) => void;
  submitting?: boolean;
}) {
  const schema = initialValues
    ? UserFormSchema.omit({ password: true }).extend(
        z.object({ password: z.string().optional() }).shape,
      )
    : UserFormSchema;

  const methods = RHF.useForm<UserFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      name: "",
      password: "",
      roleId: "",
      imageKey: null,
      ...initialValues,
    },
  });

  return (
    <FormRoot<UserFormValues>
      methods={methods}
      onSubmit={(vals) =>
        onSubmit({
          email: vals.email,
          name: vals.name,
          password: vals.password || undefined,
          roleId: vals.roleId || undefined,
          imageKey: vals.imageKey ?? undefined,
        })
      }
      className="space-y-4"
    >
      <RHF.Controller
        control={methods.control}
        name="imageKey"
        render={({ field }) => (
          <AvatarPresignedUpload
            value={field.value}
            onChange={field.onChange}
            keyPrefix="uploads/avatars"
            label="ຮູບໂປຣໄຟລ໌"
            hint="ອັບໂຫຼດຮູບໃໝ່"
          />
        )}
      />
      <FormInput
        name="email"
        label="ອີເມວ"
        type="email"
        requiredMark
        placeholder="name@example.com"
      />
      <FormInput name="name" label="ຊື່" requiredMark placeholder="John Doe" />
      <FormPassword
        name="password"
        label="ລະຫັດຜ່ານ"
        placeholder="********"
        requiredMark={!initialValues}
      />
      <FormInfiniteCombobox<RoleDTO>
        name="roleId"
        label="ບົດບາດ"
        requiredMark
        queryKey={["roles"]}
        queryFn={(args) =>
          fetchLookupForInfinite(`${config.apiUrl}/rbac/roles/lookup`, args)
        }
        preloadQueryFn={(id) =>
          hydrateLookupItem(`${config.apiUrl}/rbac/roles/lookup`, id)
        }
        getLabel={(item) => item.name}
        getValue={(item) => item.id}
        placeholder="ເລືອກບົດບາດ..."
      />

      <div className="flex justify-end gap-2">
        <Button type="submit" isLoading={submitting}>
          ບັນທຶກ
        </Button>
      </div>
    </FormRoot>
  );
}
